// Download.js - Gestion du téléchargement des documents DOCX
import axios from 'axios';
import { 
  Document, Packer, Paragraph, TextRun, AlignmentType, 
  BorderStyle, Table, TableRow, TableCell, WidthType,
  Header, PageNumber, ImageRun, VerticalAlign,
  convertInchesToTwip
} from 'docx';
import { saveAs } from 'file-saver';

// Fonction pour convertir SVG texte en PNG base64 via canvas
const svgTextToPngBase64 = (svgText, width = 150, height = 150) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      
      // Fond blanc pour le logo
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      
      // Dessiner l'image SVG
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convertir en base64
      const base64 = canvas.toDataURL('image/png').split(',')[1];
      resolve(base64);
    };
    
    img.onerror = (error) => {
      console.error('Erreur lors du chargement du logo:', error);
      reject(error);
    };
    
    // Créer un data URL à partir du SVG texte
    const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    img.src = url;
  });
};

// Fonction pour charger et convertir le logo SVG
const loadLogoPng = async () => {
  // SVG du logo de la police belge (simplifié et compatible)
  const svgContent = `<?xml version="1.0" encoding="utf-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 150 150" width="150" height="150">
  <circle cx="75" cy="75" r="74" fill="#FFFFFF" stroke="#244BA6" stroke-width="2"/>
  <path fill="#244BA6" d="M150,75c0-41.4-33.6-75-75-75S0,33.6,0,75c0,39.5,30.5,71.9,69.3,74.8c-3.3-4-7.4-9.5-11-14.6
    c-6.1-8.6-13.4-21.5-13.7-34.4c-0.2-9.6,2-14.8,14.6-34.7c4.9-7.8,8.2-21.1,7.7-23.9c-0.5-2.9,4.6,4.3,6.5,9.1
    c2.5,6.4,2.2,11.2-2.5,24.4c-2.2,6.3-5.9,16.4-2.2,20.9c-0.4-0.5,11.9-13.2,14.8-26.2c2.2-10,1.3-23.3-3-33.1
    c-3.8-8.6-8.3-15.7-7.4-27.1c0.4-4.8,1.7-9.4,1.8-9.7c2.8,7,4,9.5,5.1,11.5c5.1,9.6,15.6,23.7,20.5,33.4
    c5,9.6,8.8,25.5,3,41.7c-5.7,15.9-11.1,21.3-16.9,34.2c-4.3,9.6-7.9,21.4-9.7,28.8C117.5,149,150,115.9,150,75"/>
</svg>`;
  
  try {
    const base64 = await svgTextToPngBase64(svgContent, 100, 100);
    return base64;
  } catch (error) {
    console.warn('Erreur conversion logo:', error);
    return null;
  }
};

// Fonction pour convertir base64 en Uint8Array pour docx ImageRun
const base64ToUint8Array = (base64) => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};

// Fonction pour charger une image depuis une URL et la convertir en Uint8Array
const loadImageFromUrl = async (url) => {
  try {
    console.log('Chargement image:', url);
    
    // Si c'est déjà une data URL base64
    if (url.startsWith('data:image')) {
      const base64Data = url.split(',')[1];
      let imageType = url.match(/data:image\/(\w+)/)?.[1] || 'png';
      if (imageType === 'jpg') imageType = 'jpeg';
      console.log('Image data URL détectée, type:', imageType);
      return {
        data: base64ToUint8Array(base64Data),
        type: imageType,
      };
    }
    
    // Extraire le type d'image depuis l'extension de fichier
    const getImageTypeFromUrl = (imgUrl) => {
      const extension = imgUrl.split('.').pop()?.toLowerCase().split('?')[0];
      const typeMap = {
        'jpg': 'jpg',
        'jpeg': 'jpg',
        'png': 'png',
        'gif': 'gif',
        'webp': 'webp',
        'bmp': 'bmp',
      };
      return typeMap[extension] || 'png';
    };

    const isAbsoluteUrl = (value) => {
      try {
        // URL lèvera une erreur si la chaîne n'est pas absolue
        // eslint-disable-next-line no-new
        new URL(value);
        return true;
      } catch {
        return false;
      }
    };

    const getBackendOrigin = () => {
      const { origin } = window.location;
      // En dev, le front est souvent en 3001 et le backend en 3000 : on bascule si besoin
      if (origin.includes(':3001')) {
        return origin.replace(':3001', ':3000');
      }
      return origin;
    };
    
    // Normaliser l'URL
    let absoluteUrl = url;
    const backendOrigin = getBackendOrigin();
    
    if (isAbsoluteUrl(url)) {
      // Ne pas réécrire une URL absolue (évite de casser http://localhost:3000/uploads/…)
      absoluteUrl = url;
    } else if (url.startsWith('//')) {
      absoluteUrl = `${window.location.protocol}${url}`;
    } else if (url.startsWith('/')) {
      absoluteUrl = `${backendOrigin}${url}`;
    } else if (url.includes('/uploads/')) {
      const uploadsMatch = url.match(/\/uploads\/[^?#]+/);
      if (uploadsMatch) {
        absoluteUrl = `${backendOrigin}${uploadsMatch[0]}`;
      }
    }
    
    console.log('Chargement depuis URL:', absoluteUrl);

    const headers = {};
    const token = typeof window !== 'undefined' ? sessionStorage.getItem('authToken') : null;
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    // Charger l'image via fetch (avec auth si dispo)
    const response = await fetch(absoluteUrl, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
      headers,
    });
    
    if (!response.ok) {
      console.error('Erreur HTTP lors du chargement de l\'image:', response.status, response.statusText);
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    
    const blob = await response.blob();
    
    // Vérifier que c'est bien une image
    if (!blob.type.startsWith('image/')) {
      console.error('Le fichier téléchargé n\'est pas une image:', blob.type);
      return null;
    }
    
    // Extraire le type d'image
    let imageType = getImageTypeFromUrl(url);
    
    // Vérifier aussi le Content-Type
    if (blob.type) {
      const typeMatch = blob.type.match(/image\/(\w+)/);
      if (typeMatch) {
        let detectedType = typeMatch[1];
        imageType = detectedType;
      }
    }
    
    if (imageType === 'jpg') {
      imageType = 'jpeg';
    }
    
    console.log('Image chargée, type:', imageType, 'taille:', blob.size, 'bytes');
    
    // Vérifier que l'image a un contenu
    if (blob.size === 0) {
      console.error('Image vide téléchargée');
      return null;
    }
    
    // Convertir le blob en ArrayBuffer puis en Uint8Array
    const arrayBuffer = await blob.arrayBuffer();
    return {
      data: new Uint8Array(arrayBuffer),
      type: imageType,
    };
  } catch (error) {
    console.error('Erreur lors du chargement de l\'image:', url, error);
    return null;
  }
};

// Fonction pour formater la date et heure de début (StartDate)
const formatStartDate = (dateString) => {
  if (!dateString) return 'Non défini';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    // Format de date: jour mois année
    const dateFormatee = date.toLocaleDateString('fr-FR', {
      weekday: "long",
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    
    // Heure et minutes au format HH:MM
    const heures = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const heureFormatee = `${heures}:${minutes}`;
    
    // Ajouter un "s" à "heure" si l'heure est 2 ou plus
    const pluriel = date.getHours() >= 2 ? 's' : '';
    
    return `${dateFormatee} à ${heureFormatee} heure${pluriel}`;
  } catch (error) {
    console.error('Erreur lors du formatage de la date de début:', error);
    return dateString;
  }
};

// Fonction pour formater la date de fin (EndDate)
const formatEndDate = (dateString) => {
  if (!dateString) return 'Non défini';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    // Format de date: jour mois année
    return date.toLocaleDateString('fr-FR', {
      weekday: "long",
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Erreur lors du formatage de la date de fin:', error);
    return dateString;
  }
};

/**
 * Télécharge le rapport au format DOCX
 * @param {Object} reportData - Les données du rapport
 * @returns {Promise<{success: boolean, message: string}>} - Résultat de l'opération
 */
export const downloadReportAsDocx = async (reportData) => {
  if (!reportData) {
    return { success: false, message: 'Données du rapport manquantes' };
  }
  
  try {
    // Récupérer les données des paramètres depuis l'API
    const settingsResponse = await axios.get(`/api/settings`);
    const settings = settingsResponse.data;
    
    // Police d'écriture à utiliser pour tout le document
    const fontFamily = 'Helvetica';
    // Tailles de police en demi-points (9pt = 18, 10pt = 20, 12pt = 24, 15pt = 30, 20pt = 40)
    const fontSize9pt = 18;
    const fontSize10pt = 20;
    const fontSize12pt = 24;
    const fontSize15pt = 30;
    const fontSize20pt = 40;
    
    // Préparer les données pour l'en-tête
    const clientName = settings.clientName || 'N/A';
    const zoneNumber = settings.zoneNumber || 'N/A';
    const zoneNamePV = settings.zoneNamePV || 'N/A';
    const shortJudicialDistrict = settings.shortJudicialDistrict || 'N/A';
    const judicialDivision = settings.judicialDivision || 'N/A';
    
    // Déterminer le type de police
    let typeValue = 'N/A';
    if (settings.type === 'local') {
      typeValue = 'Locale';
    } else if (settings.type === 'federal') {
      typeValue = 'Fédérale';
    } else if (settings.type) {
      typeValue = settings.type;
    }
    
    // Convertir le logo SVG en PNG base64
    let logoImageData = null;
    try {
      const logoBase64 = await loadLogoPng();
      if (logoBase64) {
        logoImageData = base64ToUint8Array(logoBase64);
      }
    } catch (logoError) {
      console.warn('Impossible de charger le logo, le document sera généré sans logo:', logoError);
    }
    
    // Créer l'en-tête du document (répété sur chaque page)
    const createDocumentHeader = () => {
      // Table à 4 colonnes : Logo | Infos gauche | PRO JUSTITIA | Infos droite
      const noBorders = {
        top: { style: BorderStyle.NONE },
        bottom: { style: BorderStyle.NONE },
        left: { style: BorderStyle.NONE },
        right: { style: BorderStyle.NONE },
      };
      
      const headerChildren = [];
      
      // Ligne principale avec 4 colonnes
      headerChildren.push(
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          borders: {
            top: { style: BorderStyle.NONE },
            bottom: { style: BorderStyle.NONE },
            left: { style: BorderStyle.NONE },
            right: { style: BorderStyle.NONE },
            insideHorizontal: { style: BorderStyle.NONE },
            insideVertical: { style: BorderStyle.NONE },
          },
          rows: [
            new TableRow({
              children: [
                // Colonne 1: Logo
                new TableCell({
                  width: { size: 12, type: WidthType.PERCENTAGE },
                  borders: noBorders,
                  verticalAlign: VerticalAlign.CENTER,
                  children: logoImageData ? [
                    new Paragraph({
                      children: [
                        new ImageRun({
                          data: logoImageData,
                          transformation: {
                            width: 40,
                            height: 40,
                          },
                          type: 'png',
                        }),
                      ],
                    }),
                  ] : [new Paragraph({})],
                }),
                // Colonne 2: Infos gauche (aligné à gauche)
                new TableCell({
                  width: { size: 28, type: WidthType.PERCENTAGE },
                  borders: noBorders,
                  verticalAlign: VerticalAlign.CENTER,
                  children: [
                    new Paragraph({
                      spacing: { after: 40 },
                      children: [
                        new TextRun({
                          text: `Police ${typeValue}`,
                          font: fontFamily,
                          size: fontSize9pt,
                        }),
                      ],
                    }),
                    new Paragraph({
                      spacing: { after: 40 },
                      children: [
                        new TextRun({
                          text: `${clientName} - ${zoneNumber}`,
                          font: fontFamily,
                          size: fontSize9pt,
                          bold: true,
                        }),
                      ],
                    }),
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `Service ${reportData.unit}`,
                          font: fontFamily,
                          size: fontSize9pt,
                        }),
                      ],
                    }),
                  ],
                }),
                // Colonne 3: PRO JUSTITIA (centré)
                new TableCell({
                  width: { size: 30, type: WidthType.PERCENTAGE },
                  borders: noBorders,
                  verticalAlign: VerticalAlign.CENTER,
                  children: [
                    new Paragraph({
                      alignment: AlignmentType.CENTER,
                      children: [
                        new TextRun({
                          text: 'PRO JUSTITIA',
                          font: fontFamily,
                          size: fontSize15pt,
                          bold: true,
                        }),
                      ],
                    }),
                  ],
                }),
                // Colonne 4: Infos droite (aligné à droite)
                new TableCell({
                  width: { size: 30, type: WidthType.PERCENTAGE },
                  borders: noBorders,
                  verticalAlign: VerticalAlign.CENTER,
                  children: [
                    new Paragraph({
                      alignment: AlignmentType.RIGHT,
                      spacing: { after: 40 },
                      children: [
                        new TextRun({
                          text: `Procès-verbal ${reportData.reportType === "subsequent" ? "subséquent" : reportData.reportType}`,
                          font: fontFamily,
                          size: fontSize9pt,
                        }),
                      ],
                    }),
                    new Paragraph({
                      alignment: AlignmentType.RIGHT,
                      spacing: { after: 40 },
                      children: [
                        new TextRun({
                          text: `${shortJudicialDistrict}.${reportData.indice || '__'}.${judicialDivision}.${reportData.number}/${reportData.year}`,
                          font: fontFamily,
                          size: fontSize9pt,
                          bold: true,
                        }),
                      ],
                    }),
                    new Paragraph({
                      alignment: AlignmentType.RIGHT,
                      children: [
                        new TextRun({
                          text: 'Suite ',
                          font: fontFamily,
                          size: fontSize9pt,
                        }),
                        new TextRun({
                          children: [PageNumber.CURRENT],
                          font: fontFamily,
                          size: fontSize9pt,
                        }),
                        new TextRun({
                          text: ' sur ',
                          font: fontFamily,
                          size: fontSize9pt,
                        }),
                        new TextRun({
                          children: [PageNumber.TOTAL_PAGES],
                          font: fontFamily,
                          size: fontSize9pt,
                        }),
                      ],
                    }),
                  ],
                }),
              ],
            }),
          ],
        })
      );
      
      // Ligne horizontale en bas de l'en-tête (hauteur minimisée avec espace après)
      headerChildren.push(
        new Paragraph({
          border: {
            bottom: {
              color: '000000',
              space: 1,
              style: BorderStyle.SINGLE,
              size: 6,
            },
          },
          spacing: { after: 200, before: 0, line: 20 },
          children: [
            new TextRun({
              text: '',
              size: 4, // 2pt en demi-points pour minimiser la hauteur
            }),
          ],
        })
      );
      
      return new Header({
        children: headerChildren,
      });
    };
    
    // SECTION : CORPS DU RAPPORT
    
    // Introduction (centrée) - interlignes réduits pour cohérence
    const introductionParagraphs = [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 0 },
        children: [
          new TextRun({
            text: `Ce jourd'hui, le ${formatStartDate(reportData.StartDate)},`,
            font: fontFamily,
            size: fontSize10pt,
          }),
        ],
      }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 0 },
        children: [
          new TextRun({
            text: `Nous soussigné${reportData.intervenants && 
              reportData.intervenants.filter(intervenant => intervenant.sign === true).length > 1 ? 's' : ''}, `,
            font: fontFamily,
            size: fontSize10pt,
          }),
          // Ajouter les intervenants signataires avec formatage
          ...(reportData.intervenants || [])
            .filter(intervenant => intervenant.sign === true)
            .flatMap((intervenant, index, array) => {
              const segments = [];
              
              // Nom en majuscules et gras
              segments.push(new TextRun({
                text: intervenant.lastname.toUpperCase(),
                bold: true,
                font: fontFamily,
                size: fontSize10pt,
              }));
              
              segments.push(new TextRun({
                text: ' ',
                font: fontFamily,
                size: fontSize10pt,
              }));
              
              // Prénom en gras
              segments.push(new TextRun({
                text: intervenant.firstname,
                bold: true,
                font: fontFamily,
                size: fontSize10pt,
              }));
              
              segments.push(new TextRun({
                text: ', ',
                font: fontFamily,
                size: fontSize10pt,
              }));
              
              // Premier ?
              if (intervenant.premier) {
                segments.push(new TextRun({
                  text: 'Premier ',
                  font: fontFamily,
                  size: fontSize10pt,
                }));
              }
              
              // Grade
              segments.push(new TextRun({
                text: intervenant.rank,
                font: fontFamily,
                size: fontSize10pt,
              }));
              
              // Virgule si pas le dernier
              if (index < array.length - 1) {
                segments.push(new TextRun({
                  text: ', ',
                  font: fontFamily,
                  size: fontSize10pt,
                }));
              }
              
              return segments;
            }),
        ],
      }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 0 },
        children: [
          new TextRun({
            text: `attaché${reportData.intervenants && 
              reportData.intervenants.filter(intervenant => intervenant.sign === true).length > 1 ? 's' : ''} au service ${reportData.unit} de la ${zoneNamePV},`,
            font: fontFamily,
            size: fontSize10pt,
          }),
        ],
      }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 120 },
        children: [
          new TextRun({
            text: 'portons à la connaissance de votre office :',
            font: fontFamily,
            size: fontSize10pt,
          }),
        ],
      }),
      // Espace minimal avant les rubriques
      new Paragraph({ spacing: { after: 60 } }),
    ];
    
    // Fonction améliorée pour traiter le contenu HTML avec mise en forme (tables, images)
    const processHtmlForDocxWithFont = (html) => {
      if (!html) return [];
      
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      
      const paragraphs = [];
      
      // Fonction utilitaire pour convertir une valeur de couleur CSS en hex
      const parseColorValue = (colorValue) => {
        if (!colorValue) return undefined;
        
        const trimmed = colorValue.trim();
        
        if (trimmed.startsWith('#')) {
          return trimmed.substring(1);
        }
        
        // Support pour rgb()
        const rgbMatch = trimmed.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
        if (rgbMatch) {
          const r = parseInt(rgbMatch[1]).toString(16).padStart(2, '0');
          const g = parseInt(rgbMatch[2]).toString(16).padStart(2, '0');
          const b = parseInt(rgbMatch[3]).toString(16).padStart(2, '0');
          return `${r}${g}${b}`;
        }
        
        const colorMap = {
          'black': '000000',
          'white': 'FFFFFF',
          'red': 'FF0000',
          'green': '00FF00',
          'blue': '0000FF',
          'yellow': 'FFFF00',
          'gray': '808080',
          'grey': '808080',
          'purple': '800080',
          'orange': 'FFA500',
        };
        
        return colorMap[trimmed.toLowerCase()] || undefined;
      };
      
      // Fonction utilitaire pour extraire la couleur du texte (color)
      const extractColor = (style) => {
        if (!style) return undefined;
        
        // Rechercher 'color:' mais pas 'background-color:'
        const colorMatch = style.match(/(?<!background-)color\s*:\s*([^;]+)/i);
        if (colorMatch && colorMatch[1]) {
          return parseColorValue(colorMatch[1]);
        }
        
        return undefined;
      };
      
      // Fonction utilitaire pour extraire la couleur de fond (background-color)
      const extractBackgroundColor = (style) => {
        if (!style) return undefined;
        
        const bgColorMatch = style.match(/background-color\s*:\s*([^;]+)/i);
        if (bgColorMatch && bgColorMatch[1]) {
          const bgValue = bgColorMatch[1].trim().toLowerCase();
          // Ignorer les fonds transparents
          if (bgValue === 'transparent' || bgValue === 'inherit' || bgValue === 'initial') {
            return undefined;
          }
          return parseColorValue(bgColorMatch[1]);
        }
        
        return undefined;
      };
      
      // Fonction pour extraire l'alignement du texte
      const extractAlignment = (style) => {
        if (!style) return undefined;
        
        if (style.match(/text-align\s*:\s*center/i)) {
          return AlignmentType.CENTER;
        } else if (style.match(/text-align\s*:\s*right/i)) {
          return AlignmentType.RIGHT;
        } else if (style.match(/text-align\s*:\s*justify/i)) {
          return AlignmentType.JUSTIFIED;
        } else if (style.match(/text-align\s*:\s*left/i)) {
          return AlignmentType.LEFT;
        }
        
        return undefined;
      };
      
      // Fonction pour détecter si un tableau HTML a des bordures
      const tableHasBorders = (tableElement) => {
        const tableStyle = (tableElement.getAttribute('style') || '').toLowerCase();
        const tableBorder = tableElement.getAttribute('border');

        // border="0" => pas de bordure
        if (tableBorder === '0') return false;

        // Bordure définie au niveau du tableau et non "none"
        if (tableStyle.includes('border:') || tableStyle.includes('border-style:')) {
          const hasNone =
            tableStyle.includes('border:none') ||
            tableStyle.includes('border: none') ||
            tableStyle.includes('border-style:none') ||
            tableStyle.includes('border-style: none');
          if (!hasNone) return true;
        }

        // Inspecter les cellules pour détecter une bordure explicite
        const cells = tableElement.querySelectorAll('td, th');
        let hasCellBorder = false;
        for (const cell of cells) {
          const cellStyle = (cell.getAttribute('style') || '').toLowerCase();
          if (
            cellStyle.includes('border:') &&
            !cellStyle.includes('border:none') &&
            !cellStyle.includes('border: none') &&
            !cellStyle.includes('border-style:none') &&
            !cellStyle.includes('border-style: none')
          ) {
            hasCellBorder = true;
            break;
          }
        }

        if (hasCellBorder) return true;

        // Bordure via attribut border différent de 0
        if (tableBorder && tableBorder !== '0') return true;

        return false;
      };
  
  // Convertir des pixels en twips pour conserver les largeurs fixes des colonnes
  const pxToTwip = (px) => {
    const numeric = Number(px);
    if (Number.isNaN(numeric)) return null;
    return Math.round(numeric * 15); // 1px ≈ 15 twips (96dpi)
  };
      
      // Fonction pour traiter les tables HTML
  const processTable = (tableElement) => {
        const rows = [];
        const htmlRows = tableElement.querySelectorAll('tr');
        const hasBorders = tableHasBorders(tableElement);
    
    // Si aucune bordure détectée, on force l'absence de bordure (layout tables)
    const tableBorders = !hasBorders ? {
      top: { style: BorderStyle.NONE },
      bottom: { style: BorderStyle.NONE },
      left: { style: BorderStyle.NONE },
      right: { style: BorderStyle.NONE },
      insideHorizontal: { style: BorderStyle.NONE },
      insideVertical: { style: BorderStyle.NONE },
    } : undefined;
        
        // Définir les bordures selon la détection
        const cellBorders = hasBorders ? {
          top: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
          bottom: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
          left: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
          right: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
        } : {
          top: { style: BorderStyle.NONE },
          bottom: { style: BorderStyle.NONE },
          left: { style: BorderStyle.NONE },
          right: { style: BorderStyle.NONE },
        };
        
        // Récupérer les largeurs de colonnes depuis colgroup si disponible
        const colgroup = tableElement.querySelector('colgroup');
        const colWidths = [];
        if (colgroup) {
          const cols = colgroup.querySelectorAll('col');
          cols.forEach(col => {
            const colStyle = col.getAttribute('style') || '';
            const widthMatch = colStyle.match(/width\s*:\s*(\d+)px/i);
            if (widthMatch) {
              colWidths.push(parseInt(widthMatch[1]));
            } else {
              colWidths.push(null);
            }
          });
        }
        
        for (const htmlRow of htmlRows) {
          const cells = [];
          const htmlCells = htmlRow.querySelectorAll('td, th');
          
          let cellIndex = 0;
          for (const htmlCell of htmlCells) {
            const isHeader = htmlCell.tagName === 'TH';
            const cellStyle = htmlCell.getAttribute('style') || '';
            const cellAlignment = extractAlignment(cellStyle);
            
            // Calculer la largeur de la cellule si disponible
            let cellWidth = null;
            if (colWidths[cellIndex]) {
              // Convertir les pixels en pourcentage approximatif (basé sur 500px de largeur de page)
              cellWidth = Math.round((colWidths[cellIndex] / 500) * 100);
            }
            
            // Traiter le contenu de la cellule
            const cellParagraphs = [];
            
            // Chercher tous les divs dans la cellule (structure utilisée par pers-concernees, parties, etc.)
            const allDivs = htmlCell.getElementsByTagName('div');
            
            if (allDivs.length > 0) {
              // Traiter chaque div comme une ligne séparée
              for (let i = 0; i < allDivs.length; i++) {
                const divElement = allDivs[i];
                
                // Vérifier si ce div a du contenu (texte ou éléments formatés)
                const hasContent = divElement.textContent.trim().length > 0;
                
                // Ne pas traiter les divs qui sont des conteneurs de divs (éviter les doublons)
                const isContainerDiv = divElement.getElementsByTagName('div').length > 0;
                
                if (hasContent && !isContainerDiv) {
                  const divTextRuns = processTextWithFormatting(divElement, { bold: isHeader });
                  if (divTextRuns.length > 0) {
                    cellParagraphs.push(
                      new Paragraph({
                        alignment: cellAlignment,
                        spacing: { after: 0, line: 240 },
                        children: divTextRuns,
                      })
                    );
                  }
                }
              }
            }
            
            // Si aucun div n'a été trouvé ou traité, utiliser le traitement standard
            if (cellParagraphs.length === 0) {
              const cellTextRuns = processTextWithFormatting(htmlCell, { bold: isHeader });
              cellParagraphs.push(
                new Paragraph({
                  alignment: cellAlignment,
                  children: cellTextRuns.length > 0 ? cellTextRuns : [
                    new TextRun({
                      text: htmlCell.textContent.trim() || ' ',
                      font: fontFamily,
                      size: fontSize10pt,
                      bold: isHeader,
                    }),
                  ],
                })
              );
            }
            
            const cellConfig = {
              children: cellParagraphs,
              borders: cellBorders,
              verticalAlign: VerticalAlign.TOP,
            };
            
            // Ajouter la largeur si disponible
            if (cellWidth) {
              cellConfig.width = { size: cellWidth, type: WidthType.PERCENTAGE };
            }
            
            cells.push(new TableCell(cellConfig));
            cellIndex++;
          }
          
          if (cells.length > 0) {
            rows.push(new TableRow({ children: cells }));
          }
        }
        
        if (rows.length > 0) {
      const tableOptions = {
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: rows,
      };
      
      if (tableBorders) {
        tableOptions.borders = tableBorders;
      }
      
      // Appliquer les largeurs de colonnes si elles sont toutes connues
      if (colWidths.length && colWidths.every(w => typeof w === 'number')) {
        const twipWidths = colWidths.map(pxToTwip).filter(w => w !== null);
        if (twipWidths.length === colWidths.length) {
          tableOptions.columnWidths = twipWidths;
        }
      }
      
      return new Table(tableOptions);
        }
        return null;
      };
      
      // Traiter le texte avec formatage (gras, italique, couleur de fond, etc.)
      const processTextWithFormatting = (element, inheritedStyles = {}) => {
        const textRuns = [];
        
        const processNode = (node, styles = {}) => {
          if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent;
            // Garder le texte même s'il contient des espaces (mais pas s'il est vide)
            if (text && (text.trim() || text.includes(' '))) {
              // Préserver le texte original tout en normalisant les espaces multiples
              const normalizedText = text.replace(/\s+/g, ' ');
              // Ne pas ajouter de TextRun si le texte est uniquement des espaces au début ou à la fin
              if (normalizedText.trim() || textRuns.length > 0) {
                const runProps = {
                  text: normalizedText,
                  font: fontFamily,
                  size: fontSize10pt,
                  bold: styles.bold || false,
                  italics: styles.italic || false,
                  underline: styles.underline ? {} : undefined,
                  strike: styles.strike || false,
                  color: styles.color,
                };
                
                // Ajouter le surlignage si une couleur de fond est définie
                if (styles.backgroundColor) {
                  runProps.shading = {
                    type: 'clear',
                    fill: styles.backgroundColor,
                  };
                }
                
                textRuns.push(new TextRun(runProps));
              }
            }
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            const tagName = node.tagName.toLowerCase();
            const nodeStyle = node.getAttribute('style') || '';
            
            const newStyles = { ...styles };
            
            if (tagName === 'b' || tagName === 'strong' || 
                nodeStyle.includes('font-weight:bold') || nodeStyle.includes('font-weight: bold')) {
              newStyles.bold = true;
            }
            if (tagName === 'i' || tagName === 'em' || 
                nodeStyle.includes('font-style:italic') || nodeStyle.includes('font-style: italic')) {
              newStyles.italic = true;
            }
            if (tagName === 'u' || 
                nodeStyle.includes('text-decoration:underline') || nodeStyle.includes('text-decoration: underline')) {
              newStyles.underline = true;
            }
            if (tagName === 's' || tagName === 'strike' || 
                nodeStyle.includes('text-decoration:line-through') || nodeStyle.includes('text-decoration: line-through')) {
              newStyles.strike = true;
            }
            
            // Extraire la couleur du texte
            const nodeColor = extractColor(nodeStyle);
            if (nodeColor) {
              newStyles.color = nodeColor;
            }
            
            // Extraire la couleur de fond
            const nodeBgColor = extractBackgroundColor(nodeStyle);
            if (nodeBgColor) {
              newStyles.backgroundColor = nodeBgColor;
            }
            
            if (tagName === 'br') {
              textRuns.push(new TextRun({ break: 1 }));
            } else {
              for (const child of node.childNodes) {
                processNode(child, newStyles);
              }
            }
          }
        };
        
        for (const child of element.childNodes) {
          processNode(child, inheritedStyles);
        }
        
        return textRuns;
      };
      
      // Parcourir les éléments de premier niveau
      const processElement = (element) => {
        const tagName = element.tagName?.toUpperCase();
        const style = element.getAttribute?.('style') || '';
        const alignment = extractAlignment(style);
        
        if (tagName === 'TABLE') {
          const table = processTable(element);
          if (table) {
            paragraphs.push(table);
            paragraphs.push(new Paragraph({ spacing: { after: 120 } }));
          }
        } else if (tagName === 'IMG') {
          // Les images sont stockées pour traitement asynchrone ultérieur
          const src = element.getAttribute('src');
          const width = parseInt(element.getAttribute('width')) || 200;
          const height = parseInt(element.getAttribute('height')) || 150;
          
          console.log('Détection image dans HTML:', src, 'dimensions originales:', width, 'x', height);
          
          if (src) {
            // Créer un placeholder pour l'image qui sera remplacé lors du traitement asynchrone
            const imagePlaceholder = {
              isImagePlaceholder: true,
              src: src,
              width: Math.min(width, 500),
              height: Math.min(height, 400),
              alignment: alignment || AlignmentType.CENTER,
            };
            console.log('Placeholder image créé:', imagePlaceholder);
            paragraphs.push(imagePlaceholder);
          }
        } else if (tagName === 'UL') {
          const items = element.querySelectorAll(':scope > li');
          for (const item of items) {
            const textRuns = processTextWithFormatting(item);
            paragraphs.push(
              new Paragraph({
                alignment: alignment,
                children: [
                  new TextRun({ text: '• ', font: fontFamily, size: fontSize10pt }),
                  ...textRuns,
                ],
                indent: { left: 360, hanging: 240 },
              })
            );
          }
        } else if (tagName === 'OL') {
          const items = element.querySelectorAll(':scope > li');
          items.forEach((item, idx) => {
            const textRuns = processTextWithFormatting(item);
            paragraphs.push(
              new Paragraph({
                alignment: alignment,
                children: [
                  new TextRun({ text: `${idx + 1}. `, font: fontFamily, size: fontSize10pt }),
                  ...textRuns,
                ],
                indent: { left: 360, hanging: 240 },
              })
            );
          });
        } else if (tagName === 'BLOCKQUOTE') {
          const textRuns = processTextWithFormatting(element);
          paragraphs.push(
            new Paragraph({
              alignment: alignment,
              children: textRuns.length > 0 ? textRuns : [
                new TextRun({
                  text: element.textContent.trim(),
                  font: fontFamily,
                  size: fontSize10pt,
                  italics: true,
                }),
              ],
              indent: { left: 720 },
              border: {
                left: { color: '999999', size: 12, style: BorderStyle.SINGLE },
              },
            })
          );
        } else if (tagName === 'P' || tagName === 'DIV' || tagName?.match(/^H[1-6]$/)) {
          // Si le bloc contient des tableaux, on traite les enfants récursivement
          if (element.querySelector('table')) {
            for (const child of element.childNodes) {
              // On ignore les simples nœuds texte vides
              if (child.nodeType === Node.TEXT_NODE && !child.textContent.trim()) {
                continue;
              }
              if (child.nodeType === Node.ELEMENT_NODE) {
                processElement(child);
              } else if (child.nodeType === Node.TEXT_NODE) {
                paragraphs.push(
                  new Paragraph({
                    alignment: alignment,
                    children: [
                      new TextRun({
                        text: child.textContent,
                        font: fontFamily,
                        size: fontSize10pt,
                      }),
                    ],
                  })
                );
              }
            }
            return;
          }

          // Vérifier si l'élément contient des images
          const images = element.getElementsByTagName('img');
          
          if (images.length > 0) {
            // Traiter les images d'abord
            for (let i = 0; i < images.length; i++) {
              const img = images[i];
              const imgSrc = img.getAttribute('src');
              const imgWidth = parseInt(img.getAttribute('width')) || 200;
              const imgHeight = parseInt(img.getAttribute('height')) || 150;
              
              console.log('Image trouvée dans', tagName, ':', imgSrc);
              
              if (imgSrc) {
                paragraphs.push({
                  isImagePlaceholder: true,
                  src: imgSrc,
                  width: Math.min(imgWidth, 500),
                  height: Math.min(imgHeight, 400),
                  alignment: alignment || AlignmentType.CENTER,
                });
              }
            }
          }
          
          // Traiter le texte (en ignorant les images)
          const textContent = element.textContent.trim();
          if (textContent) {
            const textRuns = processTextWithFormatting(element);
            
            let headingSize = fontSize10pt;
            if (tagName?.match(/^H[1-6]$/)) {
              const level = parseInt(tagName.substring(1));
              headingSize = Math.max(fontSize10pt, 36 - (level * 4));
            }
            
            paragraphs.push(
              new Paragraph({
                alignment: alignment,
                spacing: { after: 120 },
                children: textRuns.length > 0 ? textRuns : [
                  new TextRun({
                    text: textContent,
                    font: fontFamily,
                    size: headingSize,
                  }),
                ],
              })
            );
          }
        } else if (tagName === 'BR') {
          paragraphs.push(new Paragraph({}));
        } else if (element.children && element.children.length > 0) {
          // Traiter les enfants récursivement
          for (const child of element.children) {
            processElement(child);
          }
        } else if (element.textContent?.trim()) {
          const textRuns = processTextWithFormatting(element);
          paragraphs.push(
            new Paragraph({
              alignment: alignment,
              children: textRuns.length > 0 ? textRuns : [
                new TextRun({
                  text: element.textContent.trim(),
                  font: fontFamily,
                  size: fontSize10pt,
                }),
              ],
            })
          );
        }
      };
      
      // Tags HTML considérés comme des éléments de type bloc (chacun doit devenir un paragraphe/élément séparé)
      const blockTags = new Set([
        'P', 'DIV', 'UL', 'OL', 'TABLE', 'BLOCKQUOTE',
        'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BR', 'IMG', 'HR',
      ]);
      
      // Traiter le contenu au niveau racine (peut contenir du texte mixte avec des éléments)
      const processRootContent = (container) => {
        // Vérifier si le conteneur mélange du texte inline avec des éléments bloc
        // (ex: "Nous joignons les annexes suivantes :<ul><li>…</li></ul>")
        const childNodes = Array.from(container.childNodes);
        
        const hasBlockChild = childNodes.some(node =>
          node.nodeType === Node.ELEMENT_NODE && blockTags.has(node.tagName)
        );
        
        const hasNonEmptyText = childNodes.some(node =>
          node.nodeType === Node.TEXT_NODE && node.textContent.trim()
        );
        
        const hasInlineElements = childNodes.some(node =>
          node.nodeType === Node.ELEMENT_NODE && !blockTags.has(node.tagName)
        );
        
        if (hasBlockChild && (hasNonEmptyText || hasInlineElements)) {
          // Contenu mixte : on parcourt les enfants, on regroupe le texte/inline
          // et on traite chaque élément bloc séparément
          let inlineBuffer = [];
          
          const flushInlineBuffer = () => {
            if (inlineBuffer.length === 0) return;
            // Créer un conteneur temporaire pour les nœuds inline accumulés
            const tempSpan = document.createElement('span');
            inlineBuffer.forEach(n => tempSpan.appendChild(n.cloneNode(true)));
            const textRuns = processTextWithFormatting(tempSpan);
            if (textRuns.length > 0) {
              paragraphs.push(
                new Paragraph({
                  spacing: { after: 120 },
                  children: textRuns,
                })
              );
            }
            inlineBuffer = [];
          };
          
          for (const child of childNodes) {
            if (child.nodeType === Node.ELEMENT_NODE && blockTags.has(child.tagName)) {
              // Vider le buffer inline avant de traiter l'élément bloc
              flushInlineBuffer();
              processElement(child);
            } else if (child.nodeType === Node.TEXT_NODE && !child.textContent.trim()) {
              // Ignorer les nœuds texte vides (espaces entre balises)
              continue;
            } else {
              // Texte ou élément inline : accumuler
              inlineBuffer.push(child);
            }
          }
          
          // Vider le reste du buffer inline
          flushInlineBuffer();
          
        } else {
          // Pas de mélange texte/bloc : utiliser la logique d'origine
          const hasOnlyElements = childNodes.every(node => 
            node.nodeType === Node.ELEMENT_NODE || 
            (node.nodeType === Node.TEXT_NODE && !node.textContent.trim())
          );
          
          if (hasOnlyElements && container.children.length > 0) {
            for (const child of container.children) {
              processElement(child);
            }
          } else if (container.childNodes.length > 0) {
            // Le conteneur a du texte mixte avec des éléments inline (ex: "texte <strong>gras</strong> texte")
            // Traiter tout comme un seul paragraphe avec formatage
            const textRuns = processTextWithFormatting(container);
            if (textRuns.length > 0) {
              paragraphs.push(
                new Paragraph({
                  spacing: { after: 120 },
                  children: textRuns,
                })
              );
            }
          } else if (container.textContent.trim()) {
            // Contenu texte pur
            paragraphs.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: container.textContent.trim(),
                    font: fontFamily,
                    size: fontSize10pt,
                  }),
                ],
              })
            );
          }
        }
      };
      
      processRootContent(tmp);
      
      return paragraphs;
    };
    
    // Fonction pour traiter les placeholders d'images de manière asynchrone
    const processImagePlaceholders = async (paragraphsWithPlaceholders) => {
      const processedParagraphs = [];
      
      for (const item of paragraphsWithPlaceholders) {
        if (item && item.isImagePlaceholder) {
          console.log('Traitement placeholder image:', item.src, 'dimensions:', item.width, 'x', item.height);
          
          // C'est un placeholder d'image, charger l'image
          const imageResult = await loadImageFromUrl(item.src);
          
          if (imageResult && imageResult.data && imageResult.data.length > 0) {
            console.log('Image chargée avec succès, ajout au document');
        const normalizedType = imageResult.type === 'jpg' ? 'jpeg' : imageResult.type;
            processedParagraphs.push(
              new Paragraph({
                alignment: item.alignment,
                children: [
                  new ImageRun({
                    data: imageResult.data,
                    transformation: {
                      width: item.width,
                      height: item.height,
                    },
                type: normalizedType,
                  }),
                ],
              })
            );
          } else {
            // Si l'image n'a pas pu être chargée, ajouter un placeholder texte
            console.warn('Échec du chargement de l\'image:', item.src);
            processedParagraphs.push(
              new Paragraph({
                alignment: item.alignment,
                children: [
                  new TextRun({
                    text: `[Image non disponible: ${item.src}]`,
                    font: fontFamily,
                    size: fontSize10pt,
                    italics: true,
                    color: '999999',
                  }),
                ],
              })
            );
          }
        } else {
          // C'est un paragraphe normal, le garder tel quel
          processedParagraphs.push(item);
        }
      }
      
      return processedParagraphs;
    };
    
    // Rubriques (corps du rapport)
    let rubricParagraphs = [];
    
    if (reportData.rubrics && reportData.rubrics.length > 0) {
      const sortedRubrics = [...reportData.rubrics].sort((a, b) => a.position - b.position);
      
      for (const rubric of sortedRubrics) {
        // Titre de la rubrique en 12pt gras avec barre supérieure
        rubricParagraphs.push(new Paragraph({
          border: {
            top: {
              color: '000000',
              space: 1,
              style: BorderStyle.SINGLE,
              size: 6,
            },
          },
          spacing: { before: 240, after: 120 },
          children: [
            new TextRun({
              text: rubric.title,
              bold: true,
              size: fontSize12pt,
              font: fontFamily,
            }),
          ],
        }));
        
        // Contenu de la rubrique avec formatage (peut contenir des placeholders d'images)
        const contentParagraphs = processHtmlForDocxWithFont(rubric.content);
        
        // Traiter les images de manière asynchrone
        const processedContent = await processImagePlaceholders(contentParagraphs);
        rubricParagraphs = rubricParagraphs.concat(processedContent);
        
        // Espace après la rubrique
        rubricParagraphs.push(new Paragraph({ spacing: { after: 200 } }));
      }
    }
    
    // Conclusion (alignée à droite) - interlignes réduits pour cohérence
    const conclusionParagraphs = [
      // Espace minimal avant la conclusion
      new Paragraph({ spacing: { after: 60 } }),
      
      // Texte de conclusion
      new Paragraph({
        alignment: AlignmentType.RIGHT,
        spacing: { after: 60 },
        children: [
          new TextRun({
            text: `Dont acte, clos le ${formatEndDate(reportData.EndDate)}`,
            font: fontFamily,
            size: fontSize10pt,
          }),
        ],
      }),
      
      // Espace minimal avant les signatures
      new Paragraph({ spacing: { after: 60 } }),
    ];
    
    // Signatures
    const signatureParagraphs = [];
    
    if (reportData.intervenants && reportData.intervenants.length > 0) {
      const signataires = reportData.intervenants.filter(intervenant => intervenant.sign === true);
      
      for (const intervenant of signataires) {
        signatureParagraphs.push(
          new Paragraph({
            alignment: AlignmentType.RIGHT,
            spacing: { after: 60 },
            children: [
              new TextRun({
                text: intervenant.premier ? 'Premier ' : '',
                font: fontFamily,
                size: fontSize10pt,
              }),
              new TextRun({
                text: intervenant.rank + ' ',
                font: fontFamily,
                size: fontSize10pt,
              }),
              new TextRun({
                text: intervenant.lastname.toUpperCase(),
                bold: true,
                font: fontFamily,
                size: fontSize10pt,
              }),
              new TextRun({
                text: ' ',
                font: fontFamily,
                size: fontSize10pt,
              }),
              new TextRun({
                text: intervenant.firstname,
                bold: true,
                font: fontFamily,
                size: fontSize10pt,
              }),
            ],
          })
        );
        
        // Espace pour la signature (augmenté pour laisser place à la signature)
        signatureParagraphs.push(new Paragraph({ spacing: { after: 800 } }));
      }
    }
    
    // Fusionner tous les paragraphes du corps
    const bodyParagraphs = [
      ...introductionParagraphs,
      ...rubricParagraphs,
      ...conclusionParagraphs,
      ...signatureParagraphs,
    ];
    
    // Créer le document avec en-tête répété sur chaque page
    const doc = new Document({
      creator: 'BlueNote',
      title: `PV-${shortJudicialDistrict}.${reportData.indice || '__'}.${judicialDivision}.${reportData.number}/${reportData.year}`,
      description: 'Document généré automatiquement par BlueNote',
      styles: {
        default: {
          document: {
            run: {
              font: fontFamily,
              size: fontSize10pt,
            },
            paragraph: {
              spacing: {
                line: 276,
              },
            },
          },
        },
      },
      sections: [
        {
          properties: {
            page: {
              margin: {
                top: convertInchesToTwip(1),
                bottom: convertInchesToTwip(0.75),
                left: convertInchesToTwip(1),
                right: convertInchesToTwip(1),
              },
            },
          },
          headers: {
            default: createDocumentHeader(),
          },
          children: bodyParagraphs,
        },
      ],
    });
    
    // Générer le fichier .docx
    const buffer = await Packer.toBlob(doc);
    
    // Nom du fichier
    const fileName = `PV_${shortJudicialDistrict}_${reportData.indice || '__'}_${judicialDivision}_${reportData.number}_${reportData.year}.docx`;
    
    // Télécharger le fichier
    saveAs(buffer, fileName);
    
    return { success: true, message: 'Document téléchargé' };
    
  } catch (error) {
    console.error('Erreur lors du téléchargement du document:', error);
    return { success: false, message: 'Erreur lors du téléchargement du document' };
  }
};

export default downloadReportAsDocx;
